/**
 * Card Component - プロフェッショナルデザインシステム
 * 階層構造・エレベーション・アクセシビリティ完全対応
 */

import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';
import { tokens, utils } from '../tokens';

const Card = forwardRef(({
  children,
  variant = 'default',
  elevation = 1,
  padding = 'medium',
  interactive = false,
  borderless = false,
  className = '',
  onClick,
  role,
  tabIndex,
  ariaLabel,
  ...props
}, ref) => {
  
  // バリアント別スタイル定義
  const variants = {
    default: {
      bg: tokens.colors.background.primary,
      border: tokens.colors.gray[200],
    },
    elevated: {
      bg: tokens.colors.background.elevated,
      border: 'transparent',
    },
    outline: {
      bg: tokens.colors.background.primary,
      border: tokens.colors.gray[300],
    },
    subtle: {
      bg: tokens.colors.background.secondary,
      border: tokens.colors.gray[200],
    },
  };
  
  // エレベーション（影の深さ）
  const elevations = {
    0: tokens.shadows.none,
    1: tokens.shadows.xs,
    2: tokens.shadows.sm,
    3: tokens.shadows.base,
    4: tokens.shadows.md,
    5: tokens.shadows.lg,
  };
  
  // パディングサイズ
  const paddings = {
    none: '0',
    small: tokens.spacing[4],
    medium: tokens.spacing[6],
    large: tokens.spacing[8],
  };
  
  const variantStyle = variants[variant];
  const shadow = elevations[elevation];
  const paddingValue = paddings[padding];
  
  // インタラクティブな場合のARIA属性
  const interactiveProps = interactive ? {
    role: role || 'button',
    tabIndex: tabIndex !== undefined ? tabIndex : 0,
    'aria-label': ariaLabel,
    cursor: 'pointer',
  } : {};
  
  // スタイルオブジェクト構築
  const cardStyles = {
    // レイアウト
    display: 'block',
    width: '100%',
    padding: paddingValue,
    
    // 外観
    backgroundColor: variantStyle.bg,
    border: borderless ? 'none' : `${tokens.borders.width[1]} solid ${variantStyle.border}`,
    borderRadius: tokens.borders.radius.lg,
    boxShadow: shadow,
    
    // インタラクション
    transition: `all ${tokens.transitions.duration.base} ${tokens.transitions.timing.ease}`,
    cursor: interactive ? 'pointer' : 'default',
    
    // アクセシビリティ
    outline: 'none',
    
    // フォーカススタイル（インタラクティブな場合）
    ...(interactive && {
      '&:focus-visible': {
        ...utils.focusRing(),
        transform: 'translateY(-2px)',
        boxShadow: `${tokens.shadows.md}, ${utils.focusRing().boxShadow}`,
      },
      
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: tokens.shadows.md,
        borderColor: tokens.colors.gray[300],
      },
      
      '&:active': {
        transform: 'translateY(0)',
        boxShadow: shadow,
      },
    }),
  };
  
  const handleClick = (event) => {
    if (interactive && onClick) {
      onClick(event);
    }
  };
  
  const handleKeyDown = (event) => {
    if (interactive && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick?.(event);
    }
  };
  
  return (
    <motion.div
      ref={ref}
      style={cardStyles}
      className={className}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      {...interactiveProps}
      {...props}
    >
      {children}
    </motion.div>
  );
});

Card.displayName = 'Card';

// Card内部で使用するヘッダーコンポーネント
const CardHeader = forwardRef(({
  children,
  className = '',
  borderBottom = true,
  ...props
}, ref) => {
  const headerStyles = {
    padding: `${tokens.spacing[4]} ${tokens.spacing[6]} ${borderBottom ? tokens.spacing[4] : '0'} ${tokens.spacing[6]}`,
    borderBottom: borderBottom ? `${tokens.borders.width[1]} solid ${tokens.colors.gray[200]}` : 'none',
    marginBottom: borderBottom ? tokens.spacing[4] : '0',
  };
  
  return (
    <div
      ref={ref}
      style={headerStyles}
      className={className}
      {...props}
    >
      {children}
    </div>
  );
});

CardHeader.displayName = 'CardHeader';

// Card内部で使用するコンテンツコンポーネント
const CardContent = forwardRef(({
  children,
  className = '',
  ...props
}, ref) => {
  const contentStyles = {
    padding: `0 ${tokens.spacing[6]} ${tokens.spacing[6]} ${tokens.spacing[6]}`,
  };
  
  return (
    <div
      ref={ref}
      style={contentStyles}
      className={className}
      {...props}
    >
      {children}
    </div>
  );
});

CardContent.displayName = 'CardContent';

// Card内部で使用するフッターコンポーネント
const CardFooter = forwardRef(({
  children,
  className = '',
  borderTop = true,
  ...props
}, ref) => {
  const footerStyles = {
    padding: `${borderTop ? tokens.spacing[4] : '0'} ${tokens.spacing[6]} ${tokens.spacing[6]} ${tokens.spacing[6]}`,
    borderTop: borderTop ? `${tokens.borders.width[1]} solid ${tokens.colors.gray[200]}` : 'none',
    marginTop: borderTop ? tokens.spacing[4] : '0',
  };
  
  return (
    <div
      ref={ref}
      style={footerStyles}
      className={className}
      {...props}
    >
      {children}
    </div>
  );
});

CardFooter.displayName = 'CardFooter';

// Card内部で使用するタイトルコンポーネント
const CardTitle = forwardRef(({
  children,
  level = 3,
  className = '',
  ...props
}, ref) => {
  const Tag = `h${level}`;
  
  const titleStyles = {
    margin: '0',
    fontFamily: tokens.typography.fontFamily.sans,
    fontSize: tokens.typography.fontSize.lg,
    fontWeight: tokens.typography.fontWeight.semibold,
    lineHeight: tokens.typography.lineHeight.tight,
    color: tokens.colors.text.primary,
  };
  
  return (
    <Tag
      ref={ref}
      style={titleStyles}
      className={className}
      {...props}
    >
      {children}
    </Tag>
  );
});

CardTitle.displayName = 'CardTitle';

// Card内部で使用する説明テキストコンポーネント
const CardDescription = forwardRef(({
  children,
  className = '',
  ...props
}, ref) => {
  const descriptionStyles = {
    margin: `${tokens.spacing[2]} 0 0 0`,
    fontFamily: tokens.typography.fontFamily.sans,
    fontSize: tokens.typography.fontSize.sm,
    lineHeight: tokens.typography.lineHeight.normal,
    color: tokens.colors.text.secondary,
  };
  
  return (
    <p
      ref={ref}
      style={descriptionStyles}
      className={className}
      {...props}
    >
      {children}
    </p>
  );
});

CardDescription.displayName = 'CardDescription';

export { 
  Card, 
  CardHeader, 
  CardContent, 
  CardFooter, 
  CardTitle, 
  CardDescription 
};